import { store } from './store.js';

const KEYS = {
  html: 'front_my_end_html',
  css: 'front_my_end_css',
  js: 'front_my_end_js',
  theme: 'front_my_end_theme',
  notes: 'front_my_end_notes',
  live: 'front_my_end_live',
  view: 'front_my_end_view',
  previewFrame: 'front_my_end_preview_frame'
};

function safeGet(key) {
  try {
    return localStorage.getItem(key);
  } catch (_e) {
    return null;
  }
}

/**
 * Safe localStorage mutations failing soft if cookies/storage is blocked.
 */
function safeSet(key, value) {
  try {
    localStorage.setItem(key, value);
  } catch (_e) {
    /* Storage may be unavailable (private mode / quota). Fail soft. */
  }
}

function safeRemove(key) {
  try {
    localStorage.removeItem(key);
  } catch (_e) {
    /* no-op */
  }
}

/**
 * Front-my-End — Persistence Layer (Reactive store integration)
 */
export const Storage = {
  KEYS,

  // --- Code editors ---
  getCode() {
    return {
      html: safeGet(KEYS.html),
      css: safeGet(KEYS.css),
      js: safeGet(KEYS.js)
    };
  },
  setCode(html, css, js) {
    safeSet(KEYS.html, html);
    safeSet(KEYS.css, css);
    safeSet(KEYS.js, js);
  },
  clearCode() {
    safeRemove(KEYS.html);
    safeRemove(KEYS.css);
    safeRemove(KEYS.js);
  },

  // --- Theme ---
  getTheme() {
    return safeGet(KEYS.theme);
  },
  setTheme(theme) {
    safeSet(KEYS.theme, theme);
  },

  // --- Scratch pad notes ---
  getNotes() {
    return safeGet(KEYS.notes) || '';
  },
  setNotes(notes) {
    safeSet(KEYS.notes, notes);
  },

  // --- Live preview preference ---
  getLive() {
    const v = safeGet(KEYS.live);
    // Default OFF when unset.
    return v === null ? false : v === 'true';
  },
  setLive(enabled) {
    safeSet(KEYS.live, enabled ? 'true' : 'false');
  },

  // --- Layout View Override ---
  getView() {
    return safeGet(KEYS.view) || 'auto';
  },
  setView(view) {
    safeSet(KEYS.view, view);
  },

  // --- Local preview frame preference ---
  getPreviewFrameState() {
    const raw = safeGet(KEYS.previewFrame);
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch (_e) {
      return null;
    }
  },
  setPreviewFrameState(state) {
    safeSet(KEYS.previewFrame, JSON.stringify(state));
  },

  /**
   * Wires the storage hooks into the store publication stream.
   * Eliminates direct storage.js calls inside the feature modules.
   */
  initSync() {
    store.subscribe('html', (val) => {
      if (val !== null) safeSet(KEYS.html, val);
    });
    store.subscribe('css', (val) => {
      if (val !== null) safeSet(KEYS.css, val);
    });
    store.subscribe('js', (val) => {
      if (val !== null) safeSet(KEYS.js, val);
    });
    store.subscribe('activeTheme', (val) => {
      if (val !== null) safeSet(KEYS.theme, val);
    });
    store.subscribe('liveEnabled', (val) => {
      safeSet(KEYS.live, val ? 'true' : 'false');
    });
    store.subscribe('forcedView', (val) => {
      if (val !== null) safeSet(KEYS.view, val);
    });
  }
};
